import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent, CardActions,
  Button, Chip, Skeleton, IconButton
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HistoryIcon from '@mui/icons-material/History';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import api from '../services/api';
import { useCart } from '../context/CartContext';

// 個人化推薦組件
export const PersonalizedRecommendations = ({ limit = 8, title = "為您推薦" }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const response = await api.get(`/analytics/recommendations?limit=${limit}`);
        setProducts(response.data);
      } catch (error) {
        console.error('取得推薦失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecommendations();
  }, [limit]);

  if (!loading && products.length === 0) return null;

  const calculateDiscount = (original, sale) => Math.round((1 - sale / original) * 100);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferIcon color="primary" /> {title}
      </Typography>

      <Grid container spacing={2}>
        {loading
          ? Array(4).fill(0).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={140} />
                  <CardContent>
                    <Skeleton width="80%" />
                    <Skeleton width="50%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          : products.map(product => (
              <Grid item xs={6} sm={4} md={3} key={product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': { boxShadow: 4 }
                  }}
                >
                  {/* 折扣標籤 */}
                  <Chip
                    label={`-${calculateDiscount(product.original_price, product.sale_price)}%`}
                    color="error"
                    size="small"
                    sx={{ position: 'absolute', top: 8, left: 8, fontWeight: 'bold', zIndex: 1 }}
                  />
                  {/* 推薦原因標籤 */}
                  {product.reason && (
                    <Chip
                      label={product.reason}
                      size="small"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: 'rgba(255,255,255,0.9)', fontSize: '0.7rem'
                      }}
                    />
                  )}

                  <CardMedia
                    component="img"
                    height="140"
                    image={product.image_url || 'https://via.placeholder.com/200x140?text=商品'}
                    alt={product.name}
                    sx={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
                  />

                  <CardContent sx={{ flexGrow: 1, py: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: 40,
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
                    >
                      {product.name}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                        ${product.original_price}
                      </Typography>
                      <Typography variant="subtitle1" color="error" fontWeight="bold">
                        ${product.sale_price}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 1, pt: 0 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => addToCart(product.id)}
                      disabled={product.quantity <= 0}
                    >
                      {product.quantity > 0 ? '加入購物車' : '已售完'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
        }
      </Grid>
    </Box>
  );
};

// 相關商品組件 (用於商品詳情頁)
export const RelatedProducts = ({ productId, limit = 6 }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!productId) return;
    const fetchRelated = async () => {
      try {
        const response = await api.get(`/analytics/related/${productId}?limit=${limit}`);
        setProducts(response.data);
      } catch (error) {
        console.error('取得相關商品失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRelated();
  }, [productId, limit]);

  if (!loading && products.length === 0) return null;

  const calculateDiscount = (original, sale) => Math.round((1 - sale / original) * 100);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="secondary" /> 相關商品
      </Typography>

      <Grid container spacing={2}>
        {loading
          ? Array(3).fill(0).map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={120} />
                  <CardContent>
                    <Skeleton width="80%" />
                    <Skeleton width="50%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          : products.map(product => (
              <Grid item xs={6} sm={4} md={2} key={product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': { boxShadow: 4 }
                  }}
                >
                  <Chip
                    label={`-${calculateDiscount(product.original_price, product.sale_price)}%`}
                    color="error"
                    size="small"
                    sx={{ position: 'absolute', top: 4, left: 4, fontSize: '0.7rem' }}
                  />

                  <CardMedia
                    component="img"
                    height="120"
                    image={product.image_url || 'https://via.placeholder.com/150x120?text=商品'}
                    alt={product.name}
                    sx={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
                  />

                  <CardContent sx={{ flexGrow: 1, p: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
                    >
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="error" fontWeight="bold">
                      ${product.sale_price}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
        }
      </Grid>
    </Box>
  );
};

// 最近瀏覽組件
export const RecentlyViewed = ({ limit = 6 }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const response = await api.get(`/analytics/recent-views?limit=${limit}`);
        setProducts(response.data);
      } catch (error) {
        console.error('取得最近瀏覽失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecent();
  }, [limit]);

  if (!loading && products.length === 0) return null;

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon color="action" /> 最近瀏覽
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3 }
        }}
      >
        {loading
          ? Array(4).fill(0).map((_, i) => (
              <Box key={i} sx={{ minWidth: 120 }}>
                <Skeleton variant="rectangular" width={120} height={100} />
                <Skeleton width="80%" />
              </Box>
            ))
          : products.map(product => (
              <Box
                key={product.product_id}
                sx={{
                  minWidth: 120,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
                onClick={() => navigate(`/products/${product.product_id}`)}
              >
                <Box
                  component="img"
                  src={product.image_url || 'https://via.placeholder.com/120x100?text=商品'}
                  alt={product.name}
                  sx={{ width: 120, height: 100, objectFit: 'cover', borderRadius: 1 }}
                />
                <Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 120 }}>
                  {product.name}
                </Typography>
                <Typography variant="body2" color="error" fontWeight="bold">
                  ${product.sale_price}
                </Typography>
              </Box>
            ))
        }
      </Box>
    </Box>
  );
};

// 熱門搜尋組件 (已升級 - 使用新的搜尋 API)
export const HotSearches = ({ limit = 8, onSearch }) => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHotSearches = async () => {
      try {
        // 優先使用新的搜尋 API
        const response = await api.get(`/search/trending?limit=${limit}`);
        if (response.data.success) {
          setKeywords(response.data.data);
        } else {
          // 降級到舊 API
          const fallbackRes = await api.get(`/analytics/hot-searches?limit=${limit}`);
          setKeywords(fallbackRes.data);
        }
      } catch (error) {
        console.error('取得熱門搜尋失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHotSearches();
  }, [limit]);

  if (!loading && keywords.length === 0) return null;

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TrendingUpIcon fontSize="small" /> 熱門搜尋：
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {loading
          ? Array(5).fill(0).map((_, i) => (
              <Skeleton key={i} width={60} height={24} sx={{ borderRadius: 2 }} />
            ))
          : keywords.map(item => (
              <Chip
                key={item.keyword}
                label={`${item.keyword}${item.search_count ? ` (${item.search_count})` : ''}`}
                size="small"
                variant="outlined"
                clickable
                onClick={() => onSearch && onSearch(item.keyword)}
                sx={{ '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
              />
            ))
        }
      </Box>
    </Box>
  );
};

// 基於搜尋的推薦商品組件
export const SearchBasedRecommendations = ({ keyword, limit = 8, title }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!keyword) return;

    const fetchRecommendations = async () => {
      setLoading(true);
      try {
        const sessionId = localStorage.getItem('session_id');
        const response = await api.get(`/search/recommendations?keyword=${encodeURIComponent(keyword)}&limit=${limit}`);
        if (response.data.success && response.data.data.length > 0) {
          setProducts(response.data.data);
        }
      } catch (error) {
        console.error('取得搜尋推薦失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [keyword, limit]);

  if (!loading && products.length === 0) return null;

  const calculateDiscount = (original, sale) => Math.round((1 - sale / original) * 100);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="primary" /> {title || `與「${keyword}」相關的推薦`}
      </Typography>

      <Grid container spacing={2}>
        {loading
          ? Array(4).fill(0).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={140} />
                  <CardContent>
                    <Skeleton width="80%" />
                    <Skeleton width="50%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          : products.map(product => (
              <Grid item xs={6} sm={4} md={3} key={product.product_id || product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': { boxShadow: 4 }
                  }}
                >
                  {product.sale_price && product.price && (
                    <Chip
                      label={`-${calculateDiscount(product.price, product.sale_price)}%`}
                      color="error"
                      size="small"
                      sx={{ position: 'absolute', top: 8, left: 8, fontWeight: 'bold', zIndex: 1 }}
                    />
                  )}
                  {product.score && (
                    <Chip
                      label={`推薦度 ${Math.round(product.score)}`}
                      size="small"
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: 'rgba(76, 175, 80, 0.9)', color: 'white', fontSize: '0.7rem'
                      }}
                    />
                  )}

                  <CardMedia
                    component="img"
                    height="140"
                    image={product.image_url || 'https://via.placeholder.com/200x140?text=商品'}
                    alt={product.name}
                    sx={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${product.seo_url || product.product_id || product.id}`)}
                  />

                  <CardContent sx={{ flexGrow: 1, py: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: 40,
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/products/${product.seo_url || product.product_id || product.id}`)}
                    >
                      {product.name}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {product.price && (
                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          ${product.price}
                        </Typography>
                      )}
                      <Typography variant="subtitle1" color="error" fontWeight="bold">
                        ${product.sale_price || product.price}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 1, pt: 0 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => addToCart(product.product_id || product.id)}
                    >
                      加入購物車
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
        }
      </Grid>
    </Box>
  );
};

// 智能推薦組件 (綜合多種策略)
export const SmartRecommendations = ({ limit = 10, title = "您可能喜歡" }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSmartRecs = async () => {
      try {
        const sessionId = localStorage.getItem('session_id');
        const userId = localStorage.getItem('user_id');

        if (!sessionId) return;

        const response = await api.get(`/search/recommendations?type=smart&session_id=${sessionId}${userId ? `&user_id=${userId}` : ''}&limit=${limit}`);
        if (response.data.success) {
          setProducts(response.data.data);
        }
      } catch (error) {
        console.error('取得智能推薦失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSmartRecs();
  }, [limit]);

  if (!loading && products.length === 0) return null;

  const calculateDiscount = (original, sale) => Math.round((1 - sale / original) * 100);

  return (
    <Box sx={{ my: 4 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferIcon color="secondary" /> {title}
      </Typography>

      <Grid container spacing={2}>
        {loading
          ? Array(4).fill(0).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={140} />
                  <CardContent>
                    <Skeleton width="80%" />
                    <Skeleton width="50%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          : products.map(product => (
              <Grid item xs={6} sm={4} md={3} key={product.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': { boxShadow: 4 }
                  }}
                >
                  {product.sale_price && product.price && (
                    <Chip
                      label={`-${calculateDiscount(product.price, product.sale_price)}%`}
                      color="error"
                      size="small"
                      sx={{ position: 'absolute', top: 8, left: 8, fontWeight: 'bold', zIndex: 1 }}
                    />
                  )}

                  <CardMedia
                    component="img"
                    height="140"
                    image={product.image_url || 'https://via.placeholder.com/200x140?text=商品'}
                    alt={product.name}
                    sx={{ objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
                  />

                  <CardContent sx={{ flexGrow: 1, py: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: 40,
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/products/${product.seo_url || product.id}`)}
                    >
                      {product.name}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {product.price && (
                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                          ${product.price}
                        </Typography>
                      )}
                      <Typography variant="subtitle1" color="error" fontWeight="bold">
                        ${product.sale_price || product.price}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 1, pt: 0 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => addToCart(product.id)}
                    >
                      加入購物車
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
        }
      </Grid>
    </Box>
  );
};

// 購物車推薦組件
export const CartRecommendations = ({ cartProductIds, limit = 4 }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCartRecs = async () => {
      try {
        const response = await api.post(`/analytics/cart-recommendations?limit=${limit}`, {
          productIds: cartProductIds
        });
        setProducts(response.data);
      } catch (error) {
        console.error('取得購物車推薦失敗:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCartRecs();
  }, [cartProductIds, limit]);

  if (!loading && products.length === 0) return null;

  return (
    <Box sx={{ my: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOfferIcon color="warning" /> 加購優惠
      </Typography>

      <Grid container spacing={2}>
        {loading
          ? Array(2).fill(0).map((_, i) => (
              <Grid item xs={6} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={80} />
                  <CardContent sx={{ p: 1 }}>
                    <Skeleton width="80%" />
                  </CardContent>
                </Card>
              </Grid>
            ))
          : products.map(product => (
              <Grid item xs={6} sm={3} key={product.id}>
                <Card sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                  <Box
                    component="img"
                    src={product.image_url || 'https://via.placeholder.com/60x60'}
                    alt={product.name}
                    sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, mr: 1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" noWrap>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="error" fontWeight="bold">
                      ${product.sale_price}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => addToCart(product.id)}
                      sx={{ mt: 0.5, fontSize: '0.7rem', py: 0 }}
                    >
                      + 加購
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))
        }
      </Grid>
    </Box>
  );
};
